import { parseWeeklyEmotions, WEEKLY_EMOTION_META } from './weeklyStatistics';

const EMOTIONS = Object.keys(WEEKLY_EMOTION_META);

function average(values) {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}

function recordEmotion(record) {
  return parseWeeklyEmotions(record?.response?.RES?.top_5 || record?.top_5);
}

export function getPeriodMetrics(aggregate, records = []) {
  const rows = records.length ? records.map(recordEmotion) : [parseWeeklyEmotions(aggregate?.top_5)];
  const values = EMOTIONS.reduce((result, key) => {
    result[key] = average(rows.map(row => row[key]));
    return result;
  }, {});
  const total = Object.values(values).reduce((sum, value) => sum + value, 0);
  const distribution = EMOTIONS.map(key => ({
    key,
    ...WEEKLY_EMOTION_META[key],
    value: values[key],
    percentage: total ? (values[key] / total) * 100 : 0,
  })).filter(item => item.value > 0).sort((a, b) => b.value - a.value);
  const positive = distribution.filter(item => item.tone === 'positive').reduce((sum, item) => sum + item.value, 0);
  const negative = distribution.filter(item => item.tone === 'negative').reduce((sum, item) => sum + item.value, 0);
  const balance = positive + negative;
  return {
    distribution,
    dominant: distribution[0] || { key: 'none', label: 'No data', emoji: '—', color: '#77738f', percentage: 0 },
    leastFrequent: distribution[distribution.length - 1] || null,
    positivePercentage: balance ? (positive / balance) * 100 : null,
    negativePercentage: balance ? (negative / balance) * 100 : null,
    observations: records.length,
  };
}

export function filterPeriodRecords(records = [], endDate, period) {
  if (!endDate) return records;
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(end.getTime())) return records;
  return records.filter(record => {
    const rawDate = record?.report_date || record?.created_at;
    if (!rawDate) return false;
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return false;
    return period === 'yearly'
      ? date.getFullYear() === end.getFullYear()
      : date.getFullYear() === end.getFullYear() && date.getMonth() === end.getMonth();
  });
}

export function buildPeriodTimeline(records = [], period) {
  const groups = new Map();
  records.forEach(record => {
    const rawDate = record?.report_date || record?.created_at;
    if (!rawDate) return;
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return;
    const key = period === 'yearly'
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      : date.toISOString().slice(0, 10);
    const current = groups.get(key) || { key, date, rows: [] };
    current.rows.push(recordEmotion(record));
    groups.set(key, current);
  });
  return Array.from(groups.values()).sort((a, b) => a.date - b.date).map(group => {
    const values = EMOTIONS.reduce((result, key) => {
      result[key] = average(group.rows.map(row => row[key]));
      return result;
    }, {});
    const total = Object.values(values).reduce((sum, value) => sum + value, 0);
    const positive = values.happy + values.surprise;
    return {
      key: group.key,
      label: period === 'yearly'
        ? group.date.toLocaleDateString('en-US', { month: 'short' })
        : group.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      value: total ? (positive / total) * 100 : null,
      observations: group.rows.length,
    };
  });
}

export function formatPeriodRange(startDate, period) {
  if (!startDate) return period === 'yearly' ? 'This year' : 'This month';
  const start = new Date(`${startDate}T00:00:00`);
  if (period === 'yearly') return start.getFullYear().toString();
  return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
